import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { reportPlayerImage, deletePlayerImage } from "../api/uploadPlayerImage";
import { supabase } from "../supabaseClient";
import ReportIcon from "../assets/images/ic_report.svg";

const PopupContainer = styled.div`
  position: absolute;
  top: 4rem;
  left: 50%;
  transform: translateX(-50%);
  width: 25rem;
  /* 팝업 전체 높이를 제한하여 내부 스크롤 유도 */
  max-height: 35rem;
  background: white;
  border: 0.125rem solid #545454;
  border-radius: 0.5rem;
  box-shadow: 0.5rem 0.5rem 1.5rem rgba(0, 0, 0, 0.15);
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  /* 고정 높이 섹션 */
  flex-shrink: 0; 
  padding: 1rem;
  border-bottom: 0.0625rem solid #eee;
  font-family: 'Pretendard Variable';
  font-weight: bold;
  font-size: 1.125rem;
  display: flex;
  justify-content: space-between;
`;

const ImageGrid = styled.div`
  display: grid;
  /* 컬럼 너비를 이미지 크기에 맞게 고정 (이미지 가로 6.25rem + 여백) */
  grid-template-columns: repeat(auto-fill, 6.25rem); 
  gap: 1.25rem; 
  padding: 1.25rem;
  /* 내부 스크롤 활성화 */
  overflow-y: auto; 
  justify-content: center;

  /* 스크롤바 커스텀 (선택 사항 - 백엔드 개발자 취향) */
  &::-webkit-scrollbar {
    width: 0.4rem;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 0.2rem;
  }
`;

// 이미지 배경 height랑 width를 고정
const ImageWrapper = styled.div`
  position: relative;
  /* 20:29 비율을 rem 단위로 고정 계산 (예: 6.25rem x 9.0625rem) */
  width: 6.25rem;
  height: 9.0625rem;
  background-color: #f5f5f5;
  border-radius: 0.25rem;
  overflow: hidden;
  cursor: ${(props) => (props.isDisabled ? 'not-allowed' : 'pointer')};
  flex-shrink: 0; /* 크기 유지 보장 */
  border: ${(props) => (props.isSelected ? "0.1875rem solid #A939FF" : "0.0625rem solid #ddd")};
  opacity: ${(props) => (props.isDisabled ? '0.5' : '1')};

  &:hover {
    border-color: ${(props) => (props.isDisabled ? '#ddd' : '#A939FF')};
  }
`;

const StyledImage = styled.img`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  /* 고정된 배경 안에서 비율 유지하며 최대치로 채움 */
  width: auto;         /* 가로를 카드에 맞춤 */
  height: 100%;        /* 비율 유지 */
  max-height: 100%;    /* 카드를 넘지 않게 */
  object-fit: contain;
`;

const ReportButton = styled.img`
  position: absolute;
  top: 0.1rem;
  right: 0.1rem;
  width: 1rem;
  height: 1rem;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background-color 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 0.1rem;
  right: 0.1rem;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  background-color: #FF4444;
  color: white;
  border: none;
  border-radius: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  font-size: 0.8rem;
  font-weight: bold;
  transition: background-color 0.2s;

  &:hover {
    background-color: #CC0000;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 1.5rem;
  padding: 2rem;
  text-align: center;
  overflow-y: auto;
`;

const EmptyText = styled.p`
  font-family: 'Pretendard Variable';
  font-size: 1rem;
  color: #545454;
  margin: 0;
`;

const AddButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #A939FF;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-family: 'Pretendard Variable';
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #8B2FCC;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

export default function FaceOnSelectPopup({ defaultFaceOnId, faceOnList, onSelect, onClose, onAddClick, isLoading = false }) {
  const popupRef = useRef(null);
  const [reportingImageId, setReportingImageId] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // 현재 로그인한 사용자 정보 로드
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: sessionData, error } = await supabase.auth.getSession();
      if (!error && sessionData?.session?.user?.id) {
        setCurrentUserId(sessionData.session.user.id);
      }
    };
    getCurrentUser();
  }, []);

  const handleDelete = async (e, faceOnId) => {
    e.stopPropagation();

    const confirmed = window.confirm('정말로 이 이미지를 삭제하시겠습니까?');
    if (confirmed) {
      setDeletingImageId(faceOnId);
      try {
        await deletePlayerImage(faceOnId);
        alert('이미지가 삭제되었습니다.');
        // 팝업 닫기 (부모에서 목록 새로고침하도록)
        onClose();
      } catch (error) {
        console.error('이미지 삭제 실패:', error);
        alert('이미지 삭제에 실패했습니다.');
      } finally {
        setDeletingImageId(null);
      }
    }
  };

  const handleReport = async (e, faceOnId) => {
    e.stopPropagation();

    const confirmed = window.confirm('정말로 신고하시겠습니까?\n\n허위 신고는 서비스 이용에 제한이 될 수 있습니다.');
    if (confirmed) {
      setReportingImageId(faceOnId);
      try {
        await reportPlayerImage(faceOnId);
        alert('이미지가 신고되었습니다.');
      } catch (error) {
        alert('신고 중 오류가 발생했습니다.');
      } finally {
        setReportingImageId(null);
      }
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onClose]);

  return (
    <PopupContainer ref={popupRef}>
      <Header>
        <span>이미지 선택</span>
        <span style={{ cursor: "pointer", padding: "0 0.5rem" }} onClick={onClose}>✕</span>
      </Header>
      {faceOnList.length === 0 ? (
        <EmptyContent>
          <EmptyText>아직 업로드된 이미지가 없습니다.</EmptyText>
          {onAddClick && (
            <AddButton onClick={onAddClick} disabled={isLoading}>
              이미지 추가
            </AddButton>
          )}
        </EmptyContent>
      ) : (
        <ImageGrid>
          {faceOnList.map((faceOn) => (
            <ImageWrapper
              key={faceOn.id}
              isSelected={faceOn.id === defaultFaceOnId}
              isDisabled={isLoading}
              onClick={() => !isLoading && onSelect(faceOn)}
            >
              <StyledImage src={faceOn.imageUrl} alt={`Player Face ${faceOn.id}`} />
              {faceOn.uploaderId === currentUserId ? (
                <DeleteButton
                  onClick={(e) => handleDelete(e, faceOn.id)}
                  disabled={deletingImageId === faceOn.id}
                  title="이미지 삭제"
                >
                  ✕
                </DeleteButton>
              ) : (
                <ReportButton
                  onClick={(e) => handleReport(e, faceOn.id)}
                  disabled={reportingImageId === faceOn.id}
                  title="이미지 신고"
                  src={ReportIcon}
                />
              )}
            </ImageWrapper>
          ))}
        </ImageGrid>
      )}
    </PopupContainer>
  );
}